<template>
    <v-dialog v-if="!isLoading" v-model="dialog" scrollable>
        <v-card class="my-4">
            <v-card-title>
                {{ headerText }}
            </v-card-title>
            <v-card-text>
                <div class="d-block">
                    RollenName:
                    <select-field-single
                        v-model="editRolle.rollenName.name"
                        :items="availableRollenNamen"
                        :disabled="!isNewRolle"
                    />
                </div>
                <div class="dropdown-divider" />
                Berechtigung für alle Fachbereiche und Studiengänge:
                <v-switch v-model="editRolle.isForAll" />
                <div class="dropdown-divider" />
                <div>
                    <checkbox-multi-select
                        v-if="hasRolleFachbereiche"
                        v-model="editRolle.fachbereichIds"
                        component-id="edit-rolle-fb-select"
                        :options="fachbereichStore.getFachbereiche"
                        title="Fachbereiche"
                        text-key="kurzName"
                        value-key="fachbereichId"
                    />
                    <checkbox-multi-select
                        v-if="hasRolleStudiengaenge"
                        v-model="editRolle.studiengangIds"
                        component-id="edit-rolle-stg-select"
                        :options="studiengaenge"
                        title="Studiengänge"
                        text-key="kurzName"
                        value-key="studiengangId"
                    />
                    <checkbox-multi-select
                        v-if="hasRolleModule"
                        v-model="selectedStudiengangIdForModulFilter"
                        component-id="edit-rolle-stg-filter-for-module"
                        :options="studiengaenge"
                        title="Module nach Studiengang filtern"
                        text-key="kurzName"
                        value-key="studiengangId"
                    />
                    <checkbox-multi-select
                        v-if="hasRolleModule"
                        v-model="editRolle.modulIds"
                        component-id="edit-rolle-modul-select"
                        :options="filteredModules"
                        title="Module"
                        text-key="kurzName"
                        value-key="modulId"
                        size="lg"
                    />
                </div>
            </v-card-text>
            <v-card-actions>
                <v-btn color="primary" @click="handleSaveRolle">Speichern</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts" setup>
    import axios from 'axios'
    import {
        useAlertStore,
        useFachbereichStore,
        useModuleStore,
        useStudiengangStore,
        useUserStore
    } from '@/js/stores'
    import { computed, onMounted, onUpdated, ref, watch } from 'vue'
    import CheckboxMultiSelect from '@/js/components/Common/CheckboxMultiSelect.vue'
    import { SelectFieldSingle } from '@/js/components/Common'

    const props = defineProps({
        userId: {
            type: String,
            required: true
        },
        isNewRolle: {
            type: Boolean,
            required: false,
            default: true
        },
        rolle: {
            type: Object,
            required: false,
            default: () => {
                return {
                    rollenName: { name: '' },
                    fachbereichIds: [],
                    studiengangIds: [],
                    modulIds: [],
                    isForAll: false
                }
            }
        }
    })

    const alertStore = useAlertStore()
    const userStore = useUserStore()
    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()
    const modulStore = useModuleStore()
    const editRolle = ref({})

    const rollenNamen = ref([])
    const selectedStudiengangIdForModulFilter = ref<string[]>([])

    const dialog = ref(false)

    const isLoading = computed(
        () => userStore.isLoading || studiengangStore.isLoading || fachbereichStore.isLoading
    )
    const studiengaenge = computed(() => studiengangStore.studiengaenge)
    const user = computed(() => userStore.getUserById(props.userId))

    const username = computed(() => `${user.value.vorname} ${user.value.nachname}`)
    const headerText = computed(() => {
        return props.isNewRolle
            ? `Neue Rolle für Nutzer "${username.value}" anlegen`
            : `Rolle für Nutzer "${username.value}" bearbeiten`
    })

    const filteredModules = computed(() => {
        const modules = []
        selectedStudiengangIdForModulFilter.value.forEach((studiengangId) => {
            modulStore
                .getModulesByStudiengangId(studiengangId)
                .forEach((module) => modules.push(module))
        })

        return modules
    })

    const hasRolleFachbereiche = computed(() => {
        return (
            (editRolle.value.rollenName.name === 'Studiendekan' ||
                editRolle.value.rollenName.name === 'Dekan') &&
            !editRolle.value.isForAll
        )
    })
    const hasRolleStudiengaenge = computed(
        () =>
            editRolle.value.rollenName.name === 'Studienfachberater(in)' &&
            !editRolle.value.isForAll
    )
    const hasRolleModule = computed(
        () =>
            (editRolle.value.rollenName.name === 'Modulverantwortliche(r)' ||
                editRolle.value.rollenName.name === 'Lehrende(r)') &&
            !editRolle.value.isForAll
    )

    const availableRollenNamen = computed(() => {
        if (!props.isNewRolle) {
            return [props.rolle.rollenName.name]
        }
        let rollen = Object.values(user.value.rollen)
        let existingNames = rollen.map((rolle) => rolle.rollenName.name)

        return rollenNamen.value.filter((rollenName) => !existingNames.includes(rollenName))
    })

    watch(
        () => props.rolle,
        (rolle) => {
            editRolle.value = { ...rolle }
            dialog.value = props.userId
        }
    )

    watch(
        () => editRolle.value.isForAll,
        (isForall) => {
            if (isForall) {
                editRolle.value.fachbereichIds = []
                editRolle.value.studiengangIds = []
                editRolle.value.modulIds = []
            }
        }
    )

    watch(
        () => editRolle.value.rollenName?.name,
        (newName, oldName) => {
            if (props.isNewRolle && newName !== oldName) {
                editRolle.value.fachbereichIds = []
                editRolle.value.studiengangIds = []
                editRolle.value.modulIds = []
            }
        }
    )

    onMounted(() => {
        editRolle.value = { ...props.rolle }
    })

    onUpdated(() => {
        axios.get('/api/users/rollen-namen').then((response) => {
            rollenNamen.value = response.data['hydra:member'].map((rollenName) => rollenName.name)
        })
    })

    const handleSaveRolle = async function () {
        if (editRolle.value.isForAll) {
            editRolle.value.studiengangIds = []
            editRolle.value.fachbereichIds = []
            editRolle.value.modulIds = []
        }

        await userStore.replaceRolle(
            props.userId,
            editRolle.value.rollenName.name,
            editRolle.value.fachbereichIds,
            editRolle.value.studiengangIds,
            editRolle.value.modulIds,
            editRolle.value.isForAll
        )

        if (!userStore.hasErrors) {
            alertStore.success('Rolle erfolgreich erstellt.', 'Info')
        } else {
            alertStore.error('Fehler beim Anlegen der Rolle.', 'Fehler!')
        }
    }
</script>

<style></style>
